import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  LinearProgress,
  makeStyles,
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { green, orange } from "@material-ui/core/colors";
import {
  Timer as TimerIcon,
  Send as SendIcon,
  Warning as WarningIcon,
} from "@material-ui/icons";
import api from "../../services/api";
import { isRequestCanceled } from "../../utils/requestCancel";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";

const useStyles = makeStyles((theme) => ({
  windowBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    borderBottom: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#e0e0e0"}`,
    minHeight: 48,
  },
  windowOpen: {
    backgroundColor: isDarkTheme(theme) ? alpha(green[500], 0.2) : "#e8f5e9",
  },
  windowClosed: {
    backgroundColor: isDarkTheme(theme) ? alpha(orange[500], 0.2) : "#fff3e0",
  },
  timerBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  timerText: {
    fontSize: 13,
    fontWeight: 500,
  },
  progressBar: {
    width: 100,
    height: 6,
    borderRadius: 3,
  },
  templateButton: {
    textTransform: "none",
    fontSize: 12,
  },
  closedBanner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: isDarkTheme(theme) ? alpha(orange[500], 0.2) : "#fff3e0",
    borderTop: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#e0e0e0"}`,
    gap: 8,
  },
  closedText: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: isDarkTheme(theme) ? orange[300] : "#e65100",
  },
  templateSelect: {
    minWidth: 250,
  },
  variableField: {
    marginTop: 8,
  },
}));

const formatTimeRemaining = (ms) => {
  if (ms <= 0) return i18n.t("cloudApiWindowBanner.expired");
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const WINDOW_DURATION_MS = 24 * 60 * 60 * 1000;

const CloudApiWindowBanner = ({ ticketId, whatsapp, onWindowStatusChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const [windowInfo, setWindowInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateVariables, setTemplateVariables] = useState({});
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  // Deps por valores primitivos: o Ticket recria o objeto `whatsapp` a cada
  // update de socket, e depender dele refazia as duas buscas toda vez.
  const whatsappId = whatsapp?.id;
  const whatsappType = whatsapp?.type;
  const wabaId = whatsapp?.cloudApiBusinessAccountId;
  const onWindowStatusChangeRef = useRef(onWindowStatusChange);
  useEffect(() => {
    onWindowStatusChangeRef.current = onWindowStatusChange;
  });

  // Fetch window info
  const fetchWindowInfo = useCallback(async (signal) => {
    if (!ticketId || whatsappType !== "cloudapi") return;
    try {
      const { data } = await api.get(`/cloudapi/window/${ticketId}`, { signal });
      setWindowInfo(data);
      setRemainingMs(data.remainingMs || 0);
      if (onWindowStatusChangeRef.current) {
        onWindowStatusChangeRef.current(data.isOpen);
      }
    } catch (err) {
      if (isRequestCanceled(err)) return;
      console.error("[CloudApiWindowBanner] Error fetching window info:", err);
    }
  }, [ticketId, whatsappType]);

  // Fetch templates
  const fetchTemplates = useCallback(async (signal) => {
    if (whatsappType !== "cloudapi") return;
    try {
      // Usar wabaId para carregar templates compartilhados entre números da mesma conta
      const queryParams = wabaId
        ? { wabaId }
        : { whatsappId };
      const { data } = await api.get("/message-templates", {
        params: queryParams,
        signal,
      });
      setTemplates(
        (data.templates || data).filter((t) => t.status === "APPROVED")
      );
    } catch (err) {
      if (isRequestCanceled(err)) return;
      console.error("[CloudApiWindowBanner] Error fetching templates:", err);
    }
  }, [whatsappType, wabaId, whatsappId]);

  // Uma busca por ticket/conexão (inclusive quando o ticket passa a usar outro
  // WhatsApp); abortada ao trocar de ticket.
  useEffect(() => {
    const abortController = new AbortController();
    fetchWindowInfo(abortController.signal);
    fetchTemplates(abortController.signal);
    return () => abortController.abort();
  }, [fetchWindowInfo, fetchTemplates]);

  // Countdown timer
  useEffect(() => {
    if (!windowInfo?.isOpen || remainingMs <= 0) return;

    const interval = setInterval(() => {
      setRemainingMs((prev) => {
        const newVal = prev - 1000;
        if (newVal <= 0) {
          clearInterval(interval);
          fetchWindowInfo(); // Refresh when expired
          return 0;
        }
        return newVal;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [windowInfo?.isOpen, remainingMs, fetchWindowInfo]);

  // Refresh window info periodically
  useEffect(() => {
    const interval = setInterval(() => fetchWindowInfo(), 60000);
    return () => clearInterval(interval);
  }, [fetchWindowInfo]);

  const handleOpenTemplateDialog = () => {
    setSelectedTemplate("");
    setTemplateVariables({});
    setTemplateDialogOpen(true);
  };

  const handleSelectTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === parseInt(templateId));
    if (template && template.bodyText) {
      // Extract variables like {{1}}, {{2}}, etc.
      const varMatches = template.bodyText.match(/\{\{(\d+)\}\}/g) || [];
      const vars = {};
      varMatches.forEach((v) => {
        const num = v.replace(/[{}]/g, "");
        vars[num] = "";
      });
      setTemplateVariables(vars);
    } else {
      setTemplateVariables({});
    }
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;
    setSending(true);
    try {
      const variables = Object.keys(templateVariables).length > 0
        ? Object.values(templateVariables)
        : undefined;

      await api.post(`/message-templates/${selectedTemplate}/send`, {
        ticketId,
        variables,
      });

      toast.success(i18n.t("messagesInput.cloudApi.templateSent"));
      setTemplateDialogOpen(false);
      setSelectedTemplate("");
      setTemplateVariables({});

      // Refresh window status after sending template
      setTimeout(fetchWindowInfo, 1000);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  if (!whatsapp || whatsapp.type !== "cloudapi") return null;

  const selectedTemplateObj = templates.find(
    (t) => t.id === parseInt(selectedTemplate)
  );

  // Window is open - show timer bar
  if (windowInfo?.isOpen) {
    const progress = Math.max(0, (remainingMs / WINDOW_DURATION_MS) * 100);
    return (
      <Box className={`${classes.windowBanner} ${classes.windowOpen}`}>
        <Box className={classes.timerBox}>
          <TimerIcon style={{ fontSize: 18, color: isDark ? green[300] : "#2e7d32" }} />
          <Typography className={classes.timerText} style={{ color: isDark ? green[300] : "#2e7d32" }}>
            {i18n.t("cloudApiWindowBanner.windowOpen", { time: formatTimeRemaining(remainingMs) })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            className={classes.progressBar}
            style={{
              backgroundColor: isDark ? alpha(green[500], 0.2) : "#c8e6c9",
            }}
          />
        </Box>
        <Chip
          size="small"
          label={i18n.t("cloudApiWindowBanner.officialApi")}
          style={{ ...tintStyle(isDark, green, 200, 900), fontSize: 11 }}
        />
      </Box>
    );
  }

  // Window is closed - show template prompt
  return (
    <>
      <Box className={classes.closedBanner}>
        <Typography className={classes.closedText}>
          <WarningIcon style={{ fontSize: 18 }} />
          {i18n.t("cloudApiWindowBanner.windowClosed")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<SendIcon />}
          className={classes.templateButton}
          onClick={handleOpenTemplateDialog}
          disabled={templates.length === 0}
        >
          {templates.length === 0
            ? i18n.t("cloudApiWindowBanner.noApprovedTemplates")
            : i18n.t("contactChat.templatePicker.title")}
        </Button>
      </Box>

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{i18n.t("contactChat.templatePicker.title")}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{i18n.t("messagesInput.cloudApi.selectTemplate")}</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(e) => handleSelectTemplate(e.target.value)}
              label={i18n.t("messagesInput.cloudApi.selectTemplate")}
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.displayName || t.name} ({t.language}) -{" "}
                  {t.category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTemplateObj && (
            <Box mt={2} p={2} bgcolor="background.default" border={1} borderColor="divider" borderRadius={4}>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("messagesInput.cloudApi.preview")}
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {selectedTemplateObj.bodyText || i18n.t("messagesInput.cloudApi.emptyBody")}
              </Typography>
            </Box>
          )}

          {Object.keys(templateVariables).length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("messagesInput.cloudApi.variables")}
              </Typography>
              {Object.keys(templateVariables).map((varNum) => (
                <TextField
                  key={varNum}
                  label={`${i18n.t("contactChat.templatePicker.variable")} {{${varNum}}}`}
                  value={templateVariables[varNum]}
                  onChange={(e) =>
                    setTemplateVariables((prev) => ({
                      ...prev,
                      [varNum]: e.target.value,
                    }))
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.variableField}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>{i18n.t("common.cancel")}</Button>
          <Button
            onClick={handleSendTemplate}
            color="primary"
            variant="contained"
            disabled={!selectedTemplate || sending}
          >
            {sending ? i18n.t("messagesInput.cloudApi.sending") : i18n.t("messagesInput.cloudApi.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CloudApiWindowBanner;
